/**
 * A stored chapter, as a scene rather than as a form.
 *
 * The old chapter rendered the response model: four labelled boxes in the order the API happens to declare
 * the fields — Narration, Changes, Your Action, Outcome, Situation. The player read the
 * consequence before the deed, with a caption over each. The order here is the order it
 * happened in, and the layout that expresses it is `Scene`, shared with the live turn.
 *
 * What this component adds to the shell is everything that only a *committed* chapter has: a
 * dateline the server denormalized onto the row (U4), the DM's working notes, and a change
 * ticker in its static mode over the same `changes` array the live one animated.
 */
import type { Chapter } from '@/api/generated';
import { ChangeTicker } from '@/features/story/ChangeTicker';
import { DmNotes } from '@/features/story/DmNotes';
import { Prose } from '@/features/story/Prose';
import { Scene } from '@/features/story/Scene';
import { chapterElementId } from '@/features/story/chapterAnchor';
import { datelineSegments } from '@/features/story/dateline';
import { useT } from '@/i18n/context';

interface ChapterSceneProps {
  chapter: Chapter;
}

export function ChapterScene({ chapter }: ChapterSceneProps) {
  const t = useT();

  return (
    <Scene
      id={chapterElementId(chapter.number)}
      dateline={datelineSegments(t, chapter)}
      action={chapter.action}
      notes={<DmNotes outcome={chapter.outcome} situation={chapter.situation} />}
      footer={<ChangeTicker changes={chapter.changes} />}
    >
      {/*
        U6. The live scene passes nothing — the links activate when the chapter commits, which
        is also the moment the spans exist.
      */}
      <Prose text={chapter.narration} mentions={chapter.mentions} />
    </Scene>
  );
}
